// v3.18-1378 GMAIL-002 Phase 3 — Client Gmail history card.
// Rendered on the ClientDetail Communications tab. Newest first.
import { useEffect, useState } from 'react';
import { Loader2, Mail, Paperclip, Inbox as InboxIcon, Send as SendIcon, FileEdit } from 'lucide-react';
import api from '../lib/api';

const FOLDER_ICON = { inbox: InboxIcon, sent: SendIcon, drafts: FileEdit };
const FOLDER_TINT = {
  inbox:  'bg-blue-50 text-blue-700 border-blue-200',
  sent:   'bg-emerald-50 text-emerald-700 border-emerald-200',
  drafts: 'bg-slate-100 text-slate-600 border-slate-200',
};

const fmtWhen = (iso) => (iso ? iso.slice(0, 19).replace('T', ' ') : '—');

export default function ClientGmailHistory({ clientId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!clientId) return;
      setLoading(true);
      try {
        const { data } = await api.get(`/clients/${clientId}/gmail-thread`);
        if (mounted) setItems(data?.messages || []);
      } catch {
        if (mounted) setItems([]);
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [clientId]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="client-gmail-history">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
          <Mail className="w-4 h-4" /> Gmail conversation
        </div>
        <div className="text-[10px] uppercase tracking-wider text-slate-400">Newest first</div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-slate-500 text-sm" data-testid="client-gmail-history-empty">
          No synchronized Gmail conversations for this client yet.
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {items.map((m) => {
            const Icon = FOLDER_ICON[m.folder] || Mail;
            const open = openId === m.gmail_message_id;
            return (
              <div key={m.gmail_message_id}
                   data-testid={`client-gmail-row-${m.gmail_message_id}`}
                   className="px-4 py-3">
                <button
                  onClick={() => setOpenId(open ? null : m.gmail_message_id)}
                  className="w-full text-left"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className={
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ' +
                        (FOLDER_TINT[m.folder] || FOLDER_TINT.drafts)
                      }>
                        <Icon className="w-3 h-3" /> {m.folder}
                      </span>
                      <span className="text-sm font-semibold text-slate-900">
                        {m.subject || '(no subject)'}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-500 whitespace-nowrap">
                      {fmtWhen(m.internal_date)}
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    <span className="font-semibold">{m.folder === 'sent' ? 'To' : 'From'}:</span>{' '}
                    {m.folder === 'sent' ? m.to : m.from}
                    {(m.attachments || []).length > 0 && (
                      <span className="inline-flex items-center gap-1 ml-2">
                        <Paperclip className="w-3 h-3" /> {m.attachments.length}
                      </span>
                    )}
                    {m.is_unread && (
                      <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-blue-500" title="unread" />
                    )}
                  </div>
                  {!open && (
                    <div className="text-xs text-slate-600 mt-1 line-clamp-2">
                      {m.snippet}
                    </div>
                  )}
                </button>
                {open && (
                  <div className="mt-3 border-t border-slate-100 pt-3">
                    {m.body_html ? (
                      <div className="prose prose-sm max-w-none text-slate-800"
                           dangerouslySetInnerHTML={{ __html: m.body_html }} />
                    ) : (
                      <div className="text-sm whitespace-pre-wrap text-slate-800">{m.body_text}</div>
                    )}
                    {(m.attachments || []).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {m.attachments.map((a) => (
                          <span key={a.attachment_id}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 text-xs text-slate-700">
                            <Paperclip className="w-3 h-3" /> {a.filename}
                            <span className="text-slate-400">
                              ({Math.max(1, Math.round((a.size || 0) / 1024))}KB)
                            </span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
